import React, { useState, useEffect } from 'react';
import categoryService from '../services/categoryService';
import { formatError } from '../utils/errorHelper';
import IconSelector from '../components/IconSelector';
import ColorPicker from '../components/ColorPicker';
import ConfirmDialog from '../components/ConfirmDialog';
import { getIconComponent } from '../utils/iconHelper';
import { MdEdit, MdDelete } from 'react-icons/md';
import './Categories.css';

function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [filter, setFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    type: 'expense',
    icon: 'category',
    color: '#E3A864',
    is_active: true,
  });
  const [notifications, setNotifications] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar'
  });

  useEffect(() => {
    loadCategories();
  }, []);

  // Função para adicionar notificação
  const addNotification = (type, title, message, duration = 5000) => {
    const id = Date.now();
    const notification = { id, type, title, message };
    
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remover após duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
  };

  // Função para remover notificação
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const loadCategories = async () => {
    setLoading(true);
    try {
      const data = await categoryService.getCategories();
      setCategories(data.data || data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      addNotification('error', 'Erro ao Carregar', 'Erro ao carregar categorias. Verifique sua conexão.', 6000);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData({
      ...formData,
      [name]: newValue,
    });

    // Auto-gerar slug quando o nome mudar
    if (name === 'name' && !editingCategory) {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await categoryService.updateCategory(editingCategory.id, formData);
      } else {
        await categoryService.createCategory(formData);
      }
      addNotification('success', 'Sucesso', editingCategory ? 'Categoria atualizada com sucesso!' : 'Categoria criada com sucesso!', 3000);
      setShowForm(false);
      setEditingCategory(null);
      resetForm();
      loadCategories();
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      addNotification('error', 'Erro ao Salvar', formatError(error, 'Erro ao salvar categoria. Verifique os dados.'), 6000);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      type: category.type,
      icon: category.icon || 'category',
      color: category.color || '#E3A864',
      is_active: category.is_active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Excluir Categoria',
      message: 'Deseja realmente excluir esta categoria?\n\nAtenção: Se a categoria estiver sendo usada em transações, ela não poderá ser excluída.\n\nEsta ação não pode ser desfeita.',
      type: 'error',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          await categoryService.deleteCategory(id);
          addNotification('success', 'Sucesso', 'Categoria excluída com sucesso!', 3000);
          loadCategories();
        } catch (error) {
          console.error('Erro ao excluir categoria:', error);
          addNotification('error', 'Erro ao Excluir', formatError(error, 'Erro ao excluir categoria. Ela pode estar sendo usada em transações.'), 6000);
        }
      }
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      type: 'expense',
      icon: 'category',
      color: '#E3A864',
      is_active: true,
    });
  };

  const filteredCategories = categories.filter((category) => {
    if (filter === 'all') return true;
    return category.type === filter;
  });

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="categories-page">
      <div className="page-header">
        <h1>Categorias</h1>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          + Nova Categoria
        </button>
      </div>

      <div className="filter-tabs">
        <button
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Todas ({categories.length})
        </button>
        <button
          className={`filter-tab ${filter === 'income' ? 'active' : ''}`}
          onClick={() => setFilter('income')}
        >
          Receitas ({categories.filter((c) => c.type === 'income').length})
        </button>
        <button
          className={`filter-tab ${filter === 'expense' ? 'active' : ''}`}
          onClick={() => setFilter('expense')}
        >
          Despesas ({categories.filter((c) => c.type === 'expense').length})
        </button>
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingCategory ? 'Editar' : 'Nova'} Categoria</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowForm(false);
                  setEditingCategory(null);
                  resetForm();
                }}
              >
                ✕
              </button>
            </div>
            <form className="category-form" onSubmit={handleSubmit}>
              <div className="category-form-body">
                <div className="form-group">
                  <label>Nome</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    placeholder="Ex: Alimentação"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Tipo</label>
                    <select name="type" value={formData.type} onChange={handleFormChange}>
                      <option value="expense">Despesa</option>
                      <option value="income">Receita</option>
                    </select>
                  </div>
                  <ColorPicker
                    value={formData.color}
                    onChange={(color) => setFormData({ ...formData, color })}
                    label="Cor"
                  />
                </div>

                <IconSelector
                  selectedIcon={formData.icon}
                  onSelectIcon={(icon) => setFormData({ ...formData, icon })}
                  type={formData.type}
                />

                <div className="form-group-checkbox">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleFormChange}
                  />
                  <label htmlFor="is_active">Ativa</label>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowForm(false);
                    setEditingCategory(null);
                    resetForm();
                  }}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingCategory ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="categories-grid">
        {filteredCategories.length === 0 ? (
          <div className="empty-state">
            <p>Nenhuma categoria encontrada.</p>
          </div>
        ) : (
          filteredCategories.map((category) => (
            <div key={category.id} className="category-card">
              <div className="category-header">
                <div
                  className="category-color"
                  style={{ backgroundColor: category.color }}
                >
                  {(() => {
                    const IconComponent = getIconComponent(category.icon);
                    return <IconComponent size={24} color="white" />;
                  })()}
                </div>
                <span className={`category-type ${category.type}`}>
                  {category.type === 'income' ? 'Receita' : 'Despesa'}
                </span>
              </div>
              <h3>{category.name}</h3>
              <div className="category-footer">
                <span className={`status-badge ${category.is_active ? 'active' : 'inactive'}`}>
                  {category.is_active ? 'Ativa' : 'Inativa'}
                </span>
                <div className="category-actions">
                  <button className="action-btn edit" onClick={() => handleEdit(category)} title="Editar">
                    <MdEdit />
                  </button>
                  <button className="action-btn delete" onClick={() => handleDelete(category.id)} title="Excluir">
                    <MdDelete />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Toast Notifications */}
      <div className="toast-container">
        {notifications.map((notification) => (
          <div key={notification.id} className={`toast ${notification.type}`}>
            <div className="toast-icon">
              {notification.type === 'success' && '✓'}
              {notification.type === 'error' && '✕'}
              {notification.type === 'warning' && '⚠'}
              {notification.type === 'info' && 'ℹ'}
            </div>
            <div className="toast-content">
              <h4 className="toast-title">{notification.title}</h4>
              <p className="toast-message">{notification.message}</p>
            </div>
            <button 
              className="toast-close" 
              onClick={() => removeNotification(notification.id)}
              aria-label="Fechar"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />
    </div>
  );
}

export default Categories;
